import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Product, Category } from '@/types/store';

export type { Product, Category };

// Placeholder image for products without images
const PLACEHOLDER_IMAGE = '/placeholder.svg';

// Map image URLs - use actual URL if valid, otherwise placeholder
const getProductImage = (imageUrl: string): string => {
  if (
    imageUrl &&
    (imageUrl.startsWith('/') || imageUrl.startsWith('http://') || imageUrl.startsWith('https://'))
  ) {
    return imageUrl;
  }
  return PLACEHOLDER_IMAGE;
};

interface DBProduct {
  id: string;
  name: string;
  price: number;
  original_price: number | null;
  image_url: string;
  category: string;
  description: string | null;
  sizes: string[] | null;
  colors: string[] | null;
  in_stock: boolean | null;
  has_nfc_option: boolean | null;
  nfc_price: number | null;
  is_bundle: boolean | null;
  sort_order: number | null;
  cost_price: number | null;
  created_at: string;
  updated_at: string;
}

interface DBCategory {
  id: string;
  name: string;
  product_count: number | null;
  created_at: string;
  updated_at: string;
}

export const useProducts = () => {
  return useQuery({
    queryKey: ['products'],
    queryFn: async (): Promise<Product[]> => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return ((data as DBProduct[]) || []).map((product) => ({
        id: product.id,
        name: product.name,
        price: Number(product.price),
        originalPrice: product.original_price ? Number(product.original_price) : undefined,
        image: getProductImage(product.image_url),
        category: product.category,
        description: product.description || undefined,
        sizes: product.sizes || undefined,
        colors: product.colors || undefined,
        inStock: product.in_stock ?? true,
        hasNfcOption: product.has_nfc_option ?? false,
        nfcPrice: product.nfc_price ? Number(product.nfc_price) : undefined,
        isBundle: product.is_bundle ?? false,
        sortOrder: product.sort_order ?? 0,
        costPrice: product.cost_price != null ? Number(product.cost_price) : 0,
      }));
    },
  });
};

export const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: async (): Promise<Category[]> => {
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      return ((data as DBCategory[]) || []).map((category) => ({
        id: category.id,
        name: category.name,
        productCount: category.product_count || 0,
      }));
    },
  });
};

export const useProduct = (id: string) => {
  return useQuery({
    queryKey: ['product', id],
    queryFn: async (): Promise<Product | null> => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .maybeSingle();
      
      if (error) throw error;
      if (!data) return null;
      
      const product = data as DBProduct;
      return {
        id: product.id,
        name: product.name,
        price: Number(product.price),
        originalPrice: product.original_price ? Number(product.original_price) : undefined,
        image: getProductImage(product.image_url),
        category: product.category,
        description: product.description || undefined,
        sizes: product.sizes || undefined,
        colors: product.colors || undefined,
        inStock: product.in_stock ?? true,
        hasNfcOption: product.has_nfc_option ?? false,
        nfcPrice: product.nfc_price ? Number(product.nfc_price) : undefined,
        isBundle: product.is_bundle ?? false,
        costPrice: product.cost_price != null ? Number(product.cost_price) : 0,
      };
    },
    enabled: !!id,
  });
};
